import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Text, Box } from '@mantine/core';
import { AxisWeight } from '../../types/tasteProfile';

type TasteRadarChartProps = {
  /** Section heading, e.g. "Flavor profile". */
  title: string;
  /**
   * Axis keys in render order. Any length works; the polygon adapts.
   * Pass a stable reference (a module constant, not an inline literal) — it's
   * a dependency of the layout memos.
   */
  axisKeys: readonly string[];
  /** Key to display label. Falls back to the raw key when absent. */
  axisLabels: Record<string, string>;
  weights: Record<string, AxisWeight>;
};

const SIZE = 340;
const CENTER = SIZE / 2;
const RADIUS = 110;
const LABEL_OFFSET = 32;
const GRID_LEVELS = [0.25, 0.5, 0.75, 1.0];

/** Radius of the "as often taken as passed on" ring. */
export const NEUTRAL = 0.5;

/**
 * Strength of the 50/50 prior blended into every axis, in the same units as the
 * weights themselves (so ~2 average events).
 *
 * Without it a single SAVE renders as 100% approval and spikes the polygon out
 * to the rim on one event. Shrinkage pulls thin axes toward the neutral ring in
 * proportion to how little backs them, which is both more honest and smoother
 * than a hard cutoff: there's no cliff where an axis pops into existence.
 *
 * On the reference profile it barely moves the well-evidenced axes (savory
 * 66.7% -> 66.5%, 451 units of interaction) and meaningfully corrects the thin
 * ones (smoky 66.3% -> 62.6% on 16.9 units, dropping it below fresh where the
 * raw rate had it above).
 */
export const PRIOR_STRENGTH = 5;

/**
 * Radial position for an axis, in [0, 1], where 1 is the rim.
 *
 * This IS the chart's scale — the grid rings are drawn at plain fractions of
 * RADIUS, so whatever this returns has to be readable straight off them. Don't
 * introduce a transform here without moving the rings to match.
 */
export const approvalRate = (pos: number, neg: number): number =>
  (pos + PRIOR_STRENGTH * NEUTRAL) / (pos + neg + PRIOR_STRENGTH);

const polarToCartesian = (angle: number, r: number): [number, number] => {
  // Start at top (- PI/2) and go clockwise
  const rad = (angle - 90) * (Math.PI / 180);
  return [CENTER + r * Math.cos(rad), CENTER + r * Math.sin(rad)];
};

export const TasteRadarChart = ({
  title,
  axisKeys,
  axisLabels,
  weights,
}: TasteRadarChartProps) => {
  const axisCount = axisKeys.length;
  const angleStep = 360 / axisCount;

  /**
   * WHAT THE RADIUS MEANS: the share of interactions with an axis that were
   * positive. Not the magnitude of positive weight, which is what this used to
   * plot, and not `pos - neg`.
   *
   * WHY NOT TWO POLYGONS
   * This drew posWeight in green and negWeight in red. Both counters grow with
   * how often an axis appears in what the user gets shown, so on real data the
   * two shapes correlated at r = 0.96 — the red one was a shrunken copy of the
   * green one, and the only quantity that carried preference was the ratio
   * between them, which is exactly what the eye can't extract from two nested
   * polygons.
   *
   * It also inverted the ranking. On the reference profile savory dominated
   * both polygons at pos 301 while converting at 67%, because savory is on a
   * quarter of the catalog. Rich sat near the centre at pos 56.8 and converts
   * at 85%, the strongest real preference on the profile. Dividing cancels the
   * exposure out and leaves the preference.
   *
   * Nothing in the backend reads posWeight or negWeight as independent
   * magnitudes either. generateTasteSummaryFromCounters takes their sum as a
   * confidence gate and their difference as the signal; MMR doesn't read the
   * counters at all (it scores against the pos/neg centroids, then collapses
   * those to `pos - 0.7 * neg` anyway). This chart was the only place treating
   * them as two independent quantities.
   *
   * WHY A FIXED DOMAIN
   * A rate is bounded in [0, 1], so the rings mark absolute values and the
   * neutral ring is a real reference line. The old scale normalised against the
   * largest weight on the chart, so with 301 at one end and 11.2 at the other,
   * five of seven axes collapsed into the middle and the shape mostly encoded
   * which axis happened to be biggest. Bounding it also retires the linear vs
   * sqrt question that used to live in this comment: there is no unbounded
   * accumulator left to compress.
   *
   * THE TRADE-OFF, IF YOU REVISIT THIS
   * Positive events weigh 2.0-3.5 and SKIP only -0.1, so approval rates run
   * systematically high — the reference profile spans 56% to 85% and the whole
   * polygon lives outside the neutral ring. Sub-50% is real aversion and it is
   * genuinely rare, which is why only the dots switch to red rather than the
   * fill. If that band ever feels too narrow, rescale the DOMAIN (map, say,
   * [0.4, 1.0] to the full radius) rather than bending the radial function, and
   * move the neutral ring to match. Keep the rings and the radius agreeing;
   * they didn't once before and it read as a bug.
   */
  const { axisData, points, maxTotal, hasAnyData } = useMemo(() => {
    const data = axisKeys.map((key) => {
      const w = weights[key];
      const pos = w ? w.posWeight : 0;
      const neg = w ? w.negWeight : 0;
      const total = pos + neg;
      // Undefined at total = 0, so the prior alone lands it exactly on the
      // neutral ring. An unseen axis reads as "we don't know yet" rather than
      // being pinned to the centre, which would claim active dislike.
      return { key, pos, neg, total, approval: approvalRate(pos, neg) };
    });

    const pts = data
      .map((d, i) => polarToCartesian(i * angleStep, d.approval * RADIUS).join(','))
      .join(' ');

    return {
      axisData: data,
      points: pts,
      maxTotal: Math.max(...data.map((d) => d.total), 0.01),
      hasAnyData: data.some((d) => d.total > 0),
    };
  }, [weights, angleStep, axisKeys]);

  type TooltipData = {
    x: number;
    y: number;
    label: string;
    approval: number;
    total: number;
    hasData: boolean;
  };

  const [tooltip, setTooltip] = useState<TooltipData | null>(null);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Pre-compute dot geometry, styling and tooltip payload for each axis
  const dotData = useMemo(() => {
    return axisData.map((d, i) => {
      const [px, py] = polarToCartesian(i * angleStep, d.approval * RADIUS);
      // Confidence as dot size. sqrt keeps a 20x difference in evidence from
      // becoming a 20x difference in radius.
      const r = d.total > 0 ? 3 + 4 * Math.sqrt(d.total / maxTotal) : 3.5;
      const color =
        d.total === 0
          ? 'var(--mantine-color-gray-5)'
          : d.approval < NEUTRAL
            ? 'var(--mantine-color-red-6)'
            : 'var(--mantine-color-green-6)';

      return {
        key: d.key,
        px,
        py,
        r,
        color,
        hollow: d.total === 0,
        tooltipData: {
          label: axisLabels[d.key] || d.key,
          approval: d.approval,
          total: d.total,
          hasData: d.total > 0,
          x: (px / SIZE) * 100,
          y: (py / SIZE) * 100,
        } as TooltipData,
      };
    });
  }, [axisData, angleStep, maxTotal, axisLabels]);

  // Click/tap on SVG: find nearest dot and show its tooltip
  const handleSvgClick = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      const svg = svgRef.current;
      if (!svg) return;
      const rect = svg.getBoundingClientRect();
      const svgX = ((e.clientX - rect.left) / rect.width) * SIZE;
      const svgY = ((e.clientY - rect.top) / rect.height) * SIZE;

      let nearest = dotData[0];
      let minDist = Infinity;
      for (const dot of dotData) {
        const dx = svgX - dot.px;
        const dy = svgY - dot.py;
        const dist = dx * dx + dy * dy;
        if (dist < minDist) {
          minDist = dist;
          nearest = dot;
        }
      }

      // Only trigger if tap is reasonably close (within ~60 SVG units)
      if (minDist > 3600) {
        setTooltip(null);
        setActiveKey(null);
        return;
      }

      setTooltip((prev) => {
        const isToggleOff = prev && prev.label === nearest.tooltipData.label;
        setActiveKey(isToggleOff ? null : nearest.key);
        return isToggleOff ? null : nearest.tooltipData;
      });
    },
    [dotData]
  );

  // Dismiss tooltip on click/tap outside the chart
  const handleOutsideClick = useCallback((e: MouseEvent | TouchEvent) => {
    if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
      setTooltip(null);
      setActiveKey(null);
    }
  }, []);

  useEffect(() => {
    if (tooltip) {
      document.addEventListener('mousedown', handleOutsideClick);
      document.addEventListener('touchstart', handleOutsideClick);
      return () => {
        document.removeEventListener('mousedown', handleOutsideClick);
        document.removeEventListener('touchstart', handleOutsideClick);
      };
    }
  }, [tooltip, handleOutsideClick]);

  // Nothing has been interacted with on any axis: the polygon would be a
  // perfect neutral heptagon, which says nothing.
  if (!hasAnyData) return null;

  return (
    <Box>
      <Text size="md" fw={600} mb={2}>
        {title}
      </Text>
      {/* Says what the chart means, not how it was computed. An earlier draft
          read "How often each one gets picked when it comes up", which is the
          literal definition of the scale and not how anyone thinks about their
          own taste. The rate itself still lives in the tooltip, where detail
          is welcome.

          Second sentence covers the dot sizes, which are otherwise an
          unexplained variable. It names why the encoding is there (trust the
          big ones) rather than what it measures (pos + neg, an exposure sum),
          because the reason is the part the reader can act on. */}
      <Text size="xs" c="dimmed" mb="sm">
        The further out, the more you reach for it. Bigger dots mean we know
        more about it.
      </Text>
      <Box
        ref={containerRef}
        style={{ width: '100%', maxWidth: 320, margin: '0 auto', position: 'relative' }}
      >
        <svg
          ref={svgRef}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          width="100%"
          height="100%"
          onClick={handleSvgClick}
        >
          {/* Grid rings at fixed fractions of the 0-100% domain, so a ring is a
              real value. The 50% ring is the neutral reference and is drawn
              distinctly; the rest are scale marks. */}
          {GRID_LEVELS.map((level) => {
            const isNeutral = level === NEUTRAL;
            return (
              <polygon
                key={level}
                points={axisKeys
                  .map((_, i) => polarToCartesian(i * angleStep, level * RADIUS).join(','))
                  .join(' ')}
                fill="none"
                stroke={
                  isNeutral ? 'var(--mantine-color-gray-5)' : 'var(--mantine-color-gray-3)'
                }
                strokeWidth={isNeutral || level === 1 ? 1.25 : 0.75}
                strokeDasharray={isNeutral ? '4 3' : undefined}
                opacity={isNeutral ? 0.9 : 0.6}
              />
            );
          })}

          {/* Axis lines */}
          {axisKeys.map((_, i) => {
            const [x, y] = polarToCartesian(i * angleStep, RADIUS);
            return (
              <line
                key={i}
                x1={CENTER}
                y1={CENTER}
                x2={x}
                y2={y}
                stroke="var(--mantine-color-gray-3)"
                strokeWidth={0.75}
                opacity={0.6}
              />
            );
          })}

          <polygon
            points={points}
            fill="var(--mantine-color-green-5)"
            fillOpacity={0.22}
            stroke="var(--mantine-color-green-6)"
            strokeWidth={2}
            strokeLinejoin="round"
          />

          {/* Names the neutral ring. A dashed polygon on its own reads as
              decoration, so the boundary has to say what it is. Sits on the
              ring, in the gap between the first two spokes, with a halo knocked
              out of whatever is behind it. Must paint AFTER the polygon or the
              fill covers it.

              The label has to describe the READER'S STANCE, not the food. An
              earlier draft said "so-so", which parses as a verdict on the
              dishes ("these are mediocre") rather than a position on the
              scale, and read as confusing for exactly that reason. */}
          {(() => {
            const [ax, ay] = polarToCartesian(0, NEUTRAL * RADIUS);
            const [bx, by] = polarToCartesian(angleStep, NEUTRAL * RADIUS);
            return (
              <text
                x={(ax + bx) / 2}
                y={(ay + by) / 2}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={10}
                fontWeight={500}
                fill="var(--mantine-color-gray-6)"
                stroke="var(--mantine-color-body)"
                strokeWidth={3}
                // paint-order via CSS: React's SVG attribute passthrough for
                // this one is inconsistent across versions, the property isn't.
                style={{ paintOrder: 'stroke', pointerEvents: 'none' }}
              >
                neutral
              </text>
            );
          })()}

          {/* Data points — hover for desktop (mouse only), tap handled by SVG click */}
          {dotData.map(({ key, px, py, r, color, hollow, tooltipData }) => (
            <g key={key}>
              {activeKey === key && (
                <circle cx={px} cy={py} r={r + 5} fill={color} opacity={0.25} />
              )}
              <circle
                cx={px}
                cy={py}
                r={r}
                fill={hollow ? 'var(--mantine-color-body)' : color}
                stroke={hollow ? color : undefined}
                strokeWidth={hollow ? 1.5 : undefined}
                style={{ cursor: 'pointer' }}
                onPointerEnter={(e) => {
                  if (e.pointerType === 'mouse') {
                    setTooltip(tooltipData);
                    setActiveKey(key);
                  }
                }}
                onPointerLeave={(e) => {
                  if (e.pointerType === 'mouse') {
                    setTooltip(null);
                    setActiveKey(null);
                  }
                }}
              />
            </g>
          ))}

          {/* Axis labels */}
          {axisKeys.map((key, i) => {
            const [x, y] = polarToCartesian(i * angleStep, RADIUS + LABEL_OFFSET);
            return (
              <text
                key={key}
                x={x}
                y={y}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={12}
                fontWeight={500}
                fill="var(--mantine-color-dark-4)"
              >
                {axisLabels[key] || key}
              </text>
            );
          })}
        </svg>
        {tooltip && (
          <Box
            style={{
              position: 'absolute',
              left: `${tooltip.x}%`,
              top: `${tooltip.y}%`,
              transform: 'translate(-50%, -140%)',
              backgroundColor: 'var(--mantine-color-dark-7)',
              color: 'white',
              padding: '5px 9px',
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 500,
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 3 }}>{tooltip.label}</div>
            {tooltip.hasData ? (
              <>
                <div style={{ marginBottom: 4 }}>
                  Picked {Math.round(tooltip.approval * 100)}% of the time
                </div>
                {/* Bar mirrors the radial scale: same 0-100% domain, same
                    neutral mark, so hovering can't contradict the polygon. */}
                {/* Spans the tooltip. The tooltip is absolutely positioned so
                    it shrink-to-fits its widest line, which is the sentence
                    above; a percentage width here resolves against that and
                    contributes nothing to it, so the bar tracks the text
                    rather than setting the box size. */}
                <div
                  style={{
                    position: 'relative',
                    width: '100%',
                    height: 6,
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    borderRadius: 3,
                  }}
                >
                  <div
                    style={{
                      width: `${tooltip.approval * 100}%`,
                      height: '100%',
                      backgroundColor:
                        tooltip.approval < NEUTRAL
                          ? 'var(--mantine-color-red-5)'
                          : 'var(--mantine-color-green-5)',
                      borderRadius: 3,
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      left: `${NEUTRAL * 100}%`,
                      top: -2,
                      width: 1,
                      height: 10,
                      backgroundColor: 'rgba(255,255,255,0.6)',
                    }}
                  />
                </div>
              </>
            ) : (
              <div style={{ opacity: 0.7 }}>Not enough data yet</div>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};
